/**
 * =============================================================================
 * PROMOTIONAL CAROUSELS - Theme Runtime (Catalyst copy)
 * =============================================================================
 *
 * Self-contained, pure-logic module for rendering V3 carousel themes.
 * Copied from Budflow admin — keep in sync when theme system changes.
 *
 * Zero React/DOM dependencies — pure functions only.
 */

import type { CarouselTheme, ResponsiveSize, BannerPositions } from '~/types/promotional-carousel';

// =============================================================================
// DEFAULT THEME
// =============================================================================

export const DEFAULT_THEME: CarouselTheme = {
  fonts: {
    heading: 'Inter',
    body: 'Inter',
    button: 'Inter',
  },
  colors: {
    accent: '#4ADE80',
    accentText: '#000000',
    titleText: '#FFFFFF',
    bodyText: '#9CA3AF',
    cardBackground: '#1C1C1C',
    sectionBackground: null,
  },
  title: {
    size: { mobile: '1.75rem', desktop: '2.5rem' },
    weight: '800',
    transform: 'uppercase',
    letterSpacing: '__hidden__',
    align: 'left',
  },
  subtitle: {
    size: { mobile: '0.875rem', desktop: '1rem' },
    weight: '__hidden__',
  },
  cta: {
    style: 'filled',
    borderRadius: '9999px',
    size: { mobile: 'sm', desktop: 'base' },
    weight: '700',
    transform: 'uppercase',
    letterSpacing: '-0.02em',
    padding: { x: '2rem', y: '0.875rem' },
  },
  navigation: {
    style: 'arrow',
    shape: 'circle',
    position: 'title-row',
    size: '2.5rem',
    iconColor: '#FFFFFF',
    backgroundColor: 'rgba(255,255,255,0.1)',
    hoverBackgroundColor: 'rgba(255,255,255,0.2)',
    disabledOpacity: 0.3,
    showOnMobile: false,
  },
  seeMore: {
    show: false,
    text: 'SEE MORE',
    link: '/deals',
    color: '#FFFFFF',
    size: '0.75rem',
    weight: '700',
    letterSpacing: '0.05em',
  },
  card: {
    borderRadius: '1rem',
    hoverScale: 1.015,
    titleSize: { mobile: '0.875rem', desktop: '1rem' },
    titleWeight: '700',
    titleColor: '#FFFFFF',
    titleFont: '',
    subtitleSize: { mobile: '0.75rem', desktop: '0.875rem' },
    subtitleWeight: '400',
    subtitleColor: '#9CA3AF',
    subtitleFont: '',
    gap: { mobile: '0.75rem', desktop: '1rem' },
  },
  bannerContent: {
    layout: 'centered',
    mobileLayout: 'overlay',
    verticalAlign: 'center',
    horizontalAlign: 'center',
    maxWidth: '40rem',
    overlay: {
      enabled: false,
      color: 'rgba(0,0,0,0.4)',
      gradient: 'linear-gradient(transparent, rgba(0,0,0,0.6))',
    },
    positions: {
      desktop: {
        topTitle: { x: 50, y: 8 },
        heading: { x: 50, y: 35 },
        description: { x: 50, y: 65 },
        cta: { x: 50, y: 82 },
      },
      mobile: {
        topTitle: { x: 50, y: 8 },
        heading: { x: 50, y: 30 },
        description: { x: 50, y: 60 },
        cta: { x: 50, y: 80 },
      },
    },
    topTitle: {
      size: { mobile: '0.75rem', desktop: '0.875rem' },
      weight: '600',
      color: '#FFFFFF',
      transform: 'uppercase',
      letterSpacing: '0.1em',
    },
    heading: {
      size: { mobile: '1.5rem', desktop: '3rem' },
      weight: '800',
      color: '#FFFFFF',
      transform: 'uppercase',
      lineHeight: '0.9',
    },
    description: {
      size: { mobile: '0.875rem', desktop: '1.125rem' },
      weight: '400',
      color: 'rgba(255,255,255,0.8)',
    },
  },
  dots: {
    show: false,
    style: 'filled',
    activeColor: '#FFFFFF',
    inactiveColor: 'rgba(255,255,255,0.3)',
    size: '0.5rem',
  },
  counter: {
    show: false,
    format: 'fraction',
    color: '#FFFFFF',
    size: '0.875rem',
  },
};

// =============================================================================
// RESPONSIVE BREAKPOINT RESOLUTION
// =============================================================================

/** Resolve a ResponsiveSize value for a given breakpoint with tablet→desktop fallback */
export function resolveBreakpoint(size: ResponsiveSize, mode: 'desktop' | 'tablet' | 'mobile'): string {
  if (mode === 'mobile') return size.mobile;
  if (mode === 'tablet') return size.tablet ?? size.desktop;
  return size.desktop;
}

/** Resolve positions for a given breakpoint with tablet→desktop fallback */
export function resolvePositions(positions: BannerPositions, mode: 'desktop' | 'tablet' | 'mobile'): BannerPositions['desktop'] {
  if (mode === 'mobile') return positions.mobile;
  if (mode === 'tablet') return positions.tablet ?? positions.desktop;
  return positions.desktop;
}

// =============================================================================
// THEME EXTRACTION & MERGING
// =============================================================================

/** Extract theme from any version of layout_config (returns null if no theme set) */
export function extractTheme(config: any): CarouselTheme | null {
  if (config?.version === 3 && config.theme) {
    return config.theme as CarouselTheme;
  }
  return null;
}

/** Deep merge a partial theme into the default theme (up to 3 levels) */
export function mergeThemeWithDefaults(partial: Partial<CarouselTheme> | null | undefined): CarouselTheme {
  if (!partial) return { ...DEFAULT_THEME };

  const merged = { ...DEFAULT_THEME };

  for (const key of Object.keys(DEFAULT_THEME) as (keyof CarouselTheme)[]) {
    if (partial[key] !== undefined && partial[key] !== null) {
      const defaultVal = DEFAULT_THEME[key];
      const partialVal = partial[key];
      if (typeof defaultVal === 'object' && defaultVal !== null && !Array.isArray(defaultVal)) {
        (merged as any)[key] = { ...defaultVal, ...(partialVal as any) };
        // Handle nested objects (overlay, topTitle, heading, description, padding, size)
        for (const subKey of Object.keys(defaultVal as any)) {
          const defSub = (defaultVal as any)[subKey];
          const partSub = (partialVal as any)?.[subKey];
          if (partSub !== undefined && typeof defSub === 'object' && defSub !== null && !Array.isArray(defSub)) {
            (merged as any)[key][subKey] = { ...defSub, ...partSub };
            // Level 3: merge nested objects like topTitle.size, heading.size, etc.
            for (const subSubKey of Object.keys(defSub)) {
              const defSubSub = (defSub as any)[subSubKey];
              const partSubSub = (partSub as any)?.[subSubKey];
              if (partSubSub !== undefined && typeof defSubSub === 'object' && defSubSub !== null && !Array.isArray(defSubSub)) {
                (merged as any)[key][subKey][subSubKey] = { ...defSubSub, ...partSubSub };
              }
            }
          }
        }
      } else {
        (merged as any)[key] = partialVal;
      }
    }
  }

  return merged;
}

// =============================================================================
// FONT HELPERS
// =============================================================================

export type ClientFontRecord = {
  id: string;
  family: string;
  weight: string;
  style: string;
  file_url: string;
};

function formatToCSS(src: string): string {
  if (src.endsWith('.woff2')) return 'woff2';
  if (src.endsWith('.woff')) return 'woff';
  if (src.endsWith('.otf')) return 'opentype';
  return 'truetype';
}

/** Generate @font-face CSS from an array of font records */
export function buildFontFaceCSS(fonts: ClientFontRecord[]): string {
  if (!fonts.length) return '';
  return fonts
    .map((f) => {
      const family = f.family.replace(/'/g, "\\'");
      const url = f.file_url.replace(/'/g, '%27');
      return `@font-face { font-family: '${family}'; src: url('${url}') format('${formatToCSS(f.file_url)}'); font-weight: ${f.weight || '400'}; font-style: ${f.style || 'normal'}; font-display: fallback; }`;
    })
    .join('\n');
}

/** Google Fonts that can be loaded via stylesheet link */
const GOOGLE_FONT_FAMILIES = [
  'Karla', 'Gothic A1', 'League Gothic', 'Poppins', 'Montserrat', 'Roboto',
  'Open Sans', 'Lato', 'Oswald', 'Raleway', 'DM Sans', 'Nunito', 'Barlow',
  'Sora', 'Plus Jakarta Sans', 'Outfit', 'Playfair Display', 'Merriweather',
  'Space Mono', 'Space Grotesk', 'Inter', 'Bebas Neue', 'Anton', 'Archivo',
  'Archivo Black', 'Titillium Web', 'Rubik', 'Fira Sans', 'Source Sans 3',
  'IBM Plex Sans', 'Manrope', 'Lexend', 'Urbanist',
];

/** Get Google Fonts CSS link URL for fonts that need it */
export function getGoogleFontsURL(fontFamilies: string[]): string | null {
  const needed = fontFamilies.filter((f) => GOOGLE_FONT_FAMILIES.includes(f));
  if (needed.length === 0) return null;
  const families = needed.map((f) => `family=${f.replace(/ /g, '+')}:wght@300;400;500;600;700;800;900`).join('&');
  return `https://fonts.googleapis.com/css2?${families}&display=swap`;
}

// =============================================================================
// PARSE HELPERS
// =============================================================================

/** Parse navigation metadata encoded in theme.navigation.hoverBackgroundColor */
export function parseNavMeta(hoverBgColor: string) {
  // Legacy format: plain color value like "rgba(255,255,255,0.2)" — treat as hover bg
  const isLegacy = !hoverBgColor.includes('icon:') && !hoverBgColor.includes('hoverbg:') && !hoverBgColor.includes('hoverop:');
  if (isLegacy && hoverBgColor) {
    return { iconSize: '1rem', border: '', hoverBg: hoverBgColor, hoverBorder: '', hoverIcon: '', hoverOpacity: '' };
  }
  const parts = hoverBgColor.includes('|') ? hoverBgColor.split('|') : [hoverBgColor];
  const iconSize = parts.find(p => p.startsWith('icon:'))?.slice(5) || '1rem';
  const border = parts.find(p => p.startsWith('border:'))?.slice(7) || '';
  const hoverBg = parts.find(p => p.startsWith('hoverbg:'))?.slice(8) || '';
  const hoverBorder = parts.find(p => p.startsWith('hoverborder:'))?.slice(12) || '';
  const hoverIcon = parts.find(p => p.startsWith('hovericon:'))?.slice(10) || '';
  const hoverOpacity = parts.find(p => p.startsWith('hoverop:'))?.slice(8) || '';
  return { iconSize, border, hoverBg, hoverBorder, hoverIcon, hoverOpacity };
}

/** Parse "See More" metadata encoded in theme.seeMore.letterSpacing */
export function parseSeeMoreMeta(letterSpacing: string) {
  const parts = letterSpacing.includes('|')
    ? letterSpacing.split('|')
    : letterSpacing.includes(':')
      ? [letterSpacing]
      : [];
  const hasBg = parts.some((p) => p.startsWith('bg:'));
  const bgColor = parts.find((p) => p.startsWith('bg:'))?.slice(3) || '#4ADE80';
  const radius = parts.find((p) => p.startsWith('r:'))?.slice(2) || '9999px';
  const px = parts.find((p) => p.startsWith('px:'))?.slice(3) || '16';
  const py = parts.find((p) => p.startsWith('py:'))?.slice(3) || '6';
  const mobilePosition = parts.find((p) => p.startsWith('mpos:'))?.slice(5) || 'title-row';
  const tabletPosition = parts.find((p) => p.startsWith('tpos:'))?.slice(5) || 'title-row';
  const desktopPosition = parts.find((p) => p.startsWith('dpos:'))?.slice(5) || 'title-row';
  const mobileAlign = parts.find((p) => p.startsWith('malign:'))?.slice(7) || 'center';
  const smBorder = parts.find((p) => p.startsWith('sborder:'))?.slice(8) || '';
  const maxWidth = parts.find((p) => p.startsWith('maxw:'))?.slice(5) || '';
  const mobileWidth = parts.find((p) => p.startsWith('mw:'))?.slice(3) || '85';
  const mobileSize = parts.find((p) => p.startsWith('msize:'))?.slice(6) || '';
  const hoverBg = parts.find((p) => p.startsWith('hoverbg:'))?.slice(8) || '';
  const hoverText = parts.find((p) => p.startsWith('hovertext:'))?.slice(10) || '';
  const hoverBorder = parts.find((p) => p.startsWith('hoverborder:'))?.slice(12) || '';
  const hoverOpacity = parts.find((p) => p.startsWith('hoverop:'))?.slice(8) || '';
  const tabletSize = parts.find((p) => p.startsWith('tsize:'))?.slice(6) || '';
  return { hasBg, bgColor, radius, px, py, mobilePosition, tabletPosition, desktopPosition, mobileAlign, smBorder, maxWidth, mobileWidth, mobileSize, tabletSize, hoverBg, hoverText, hoverBorder, hoverOpacity };
}

/** Parse CTA metadata encoded in theme.cta.letterSpacing */
export function parseCtaMeta(letterSpacing: string | undefined) {
  const parts = letterSpacing?.includes('|')
    ? letterSpacing.split('|')
    : letterSpacing?.includes(':')
      ? [letterSpacing]
      : [];
  const hoverOpacity = parts.find(p => p.startsWith('hoverop:'))?.slice(8) || '';
  const hoverBg = parts.find(p => p.startsWith('hoverbg:'))?.slice(8) || '';
  const hoverText = parts.find(p => p.startsWith('hovertext:'))?.slice(10) || '';
  const hoverBorder = parts.find(p => p.startsWith('hoverborder:'))?.slice(12) || '';
  return { hoverOpacity, hoverBg, hoverText, hoverBorder };
}

/** Parse responsive title alignment */
export function parseTitleAlign(align: string | undefined, breakpoint: 'desktop' | 'tablet' | 'mobile'): string {
  const rawAlign = align || 'left';
  const desktop = rawAlign.split('|')[0];
  if (breakpoint === 'tablet') {
    return rawAlign.includes('|tablet:') ? rawAlign.split('|tablet:')[1].split('|')[0] : desktop;
  }
  if (breakpoint === 'mobile') {
    return rawAlign.includes('|mobile:') ? rawAlign.split('|mobile:')[1].split('|')[0] : desktop;
  }
  return desktop;
}

/** Default banner content positions */
export const DEFAULT_POSITIONS = {
  desktop: {
    topTitle: { x: 50, y: 8 },
    heading: { x: 50, y: 35 },
    description: { x: 50, y: 65 },
    cta: { x: 50, y: 82 },
  },
  tablet: {
    topTitle: { x: 50, y: 8 },
    heading: { x: 50, y: 35 },
    description: { x: 50, y: 65 },
    cta: { x: 50, y: 82 },
  },
  mobile: {
    topTitle: { x: 50, y: 8 },
    heading: { x: 50, y: 30 },
    description: { x: 50, y: 60 },
    cta: { x: 50, y: 80 },
  },
} as const;
